import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { applyOAuthRedirect, linkDevice } from '../utils/auth';
import { getDeviceId } from '../utils/api';

/**
 * Handles the redirect from Supabase Auth after Google sign-in.
 *
 * URL shape: /auth-callback#access_token=<jwt>&refresh_token=<jwt>&…
 * - Hands the full URL to applyOAuthRedirect() which calls
 *   supabase.auth.setSession() and mirrors the access_token into the legacy
 *   localStorage key our API client reads.
 * - Links any device-scoped meets to the new user (idempotent).
 * - Routes to the dashboard.
 *
 * Uses useRef to make the exchange idempotent under React StrictMode.
 */
export default function AuthCallback({ onAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const fullUrl =
        (typeof window !== 'undefined' && window.location.href) ||
        `${location.pathname}${location.search}${location.hash}`;
      const hash = location.hash || (typeof window !== 'undefined' ? window.location.hash : '') || '';
      if (!hash.includes('access_token=')) {
        navigate('/login', { replace: true });
        return;
      }
      try {
        const user = await applyOAuthRedirect(fullUrl);
        const deviceId = getDeviceId();
        if (deviceId) await linkDevice(deviceId);
        if (onAuthenticated) onAuthenticated(user);
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Auth callback failed:', err);
        navigate('/login', { replace: true, state: { error: err.message } });
      }
    };
    run();
  }, [location, navigate, onAuthenticated]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background"
      data-testid="auth-callback-loading"
    >
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm">Signing you in…</p>
      </div>
    </div>
  );
}
